import React from 'react';
import { RouteObject, matchPath } from 'react-router-dom';
import loadable from '@loadable/component';

export type LayoutType = 'none' | 'bisection' | 'trisection';

type pageNames =
  | 'login'
  | 'main'
  | '404'
  | 'error';

export type PathObject = Record<pageNames, string>;
export interface Path extends RouteObject {
  title: string;
  key: string;
  name?: string;
  auth?: boolean;
  public?: boolean;
  layout?: LayoutType;
  children?: Path[];
  [propsName: string]: any;
  isShow?: boolean;
  rule?: string;
}

const Login = loadable(() => import('components/pages/auth/Login'));
const LoginGoogle = loadable(() => import('components/pages/auth/Google'));
const OAuth2CallBack = loadable(() => import('components/pages/auth/OAuth2Callback'));
const Register = loadable(() => import('components/pages/auth/Register'));
const Activate = loadable(() => import('components/pages/auth/Activate'));
const ForgotPassword = loadable(() => import('components/pages/auth/Password/forgot'));
const ResetPassword = loadable(() => import('components/pages/auth/Password/reset'));
const MyPage = loadable(() => import('components/pages/mypage'));
const User = loadable(() => import('components/pages/user'));
const Error404 = loadable(() => import('components/pages/Error404'));
const ErrorAuth = loadable(() => import('components/pages/ErrorAuth'));
const Lecture = loadable(() => import('components/pages/b2c/lecture'));
const LectureStudent = loadable(() => import('components/pages/b2c/lecture/student'));
const PmlStudent = loadable(() => import('components/pages/b2c/pml/student'));
const SeminarStudent = loadable(() => import('components/pages/b2c/seminar/student'));
const Seminar = loadable(() => import('components/pages/b2c/seminar'));
const Pml = loadable(() => import('components/pages/b2c/pml'));
const LectureCommon = loadable(() => import('components/pages/b2c/common/lecture'));
const SeminarCommon = loadable(() => import('components/pages/b2c/common/seminar'));
const MailCommon = loadable(() => import('components/pages/b2c/common/mail'));
const BizCategory = loadable(() => import('components/pages/common/biz/category'));
const LectureGubun = loadable(() => import('components/pages/common/lecture/gubun'));
const LectureStudio = loadable(() => import('components/pages/common/lecture/studio'));
const LectureSubject = loadable(() => import('components/pages/common/lecture/subject'));
const LectureTarget = loadable(() => import('components/pages/common/lecture/target'));
const LecturerGroup = loadable(() => import('components/pages/common/lecturer/group'));
const Company = loadable(() => import('components/pages/customer/company'));
const CompanyPoint = loadable(() => import('components/pages/customer/company/point'));
const CompanyMoney = loadable(() => import('components/pages/customer/company/money'));
const Crm = loadable(() => import('components/pages/customer/crm'));
const Mailing = loadable(() => import('components/pages/customer/mailing'));
const Operate = loadable(() => import('components/pages/operate'));
const ProjectAsk = loadable(() => import('components/pages/project/ask'));
const ProjectConfirm = loadable(() => import('components/pages/project/confirm'));
const ProjectConsult = loadable(() => import('components/pages/project/consult'));
const ProjectDrop = loadable(() => import('components/pages/project/drop'));
const Schedule = loadable(() => import('components/pages/schedule'));
const Lecturer = loadable(() => import('components/pages/lecturer'));
const BillPublic = loadable(() => import('components/pages/parment/bill_public'));
const LecturerPayment = loadable(() => import('components/pages/parment/lecturer_payment'));
const Outsourcing = loadable(() => import('components/pages/parment/outsourcing'));
const Main = loadable(() => import('components/pages/main'));
const SalesPirce = loadable(() => import('components/pages/management/sales_type'));
const SalesLecture = loadable(() => import('components/pages/management/sales_nps'));

const paths: Path[] = [
  {
    title: 'login',
    name: 'login',
    path: '/auth/login',
    key: '/auth/login',
    element: <Login />,
    isShow: false,
    public: true,
  },
  {
    title: 'LoginGoogle',
    name: 'LoginGoogle',
    path: '/auth/google',
    key: '/auth/google',
    element: <LoginGoogle />,
    isShow: false,
    public: true,
  },
  {
    title: 'OAuth2CallBack',
    name: 'OAuth2CallBack',
    path: '/oauth2callback',
    key: '/oauth2callback',
    element: <OAuth2CallBack />,
    isShow: false,
    public: true,
  },
  {
    title: 'register',
    name: 'register',
    path: '/auth/register',
    key: '/auth/register',
    element: <Register />,
    isShow: false,
    public: true,
  },  
  {
    title: 'activate',
    name: 'activate',
    path: '/auth/activate',
    key: '/auth/activate',
    element: <Activate />,
    isShow: false,
    public: true,
  },    
  {
    title: 'forgot_password',
    name: 'forgot_password',
    path: '/auth/forgot_password',
    key: '/auth/forgot_password',
    element: <ForgotPassword />,
    isShow: false,
    public: true,
  },   
  {
    title: 'reset_password',
    name: 'reset_password',
    path: '/auth/reset_password',
    key: '/auth/reset_password',
    element: <ResetPassword />,
    isShow: false,
    public: true,
  },  
  {
    path: '/',
    title: 'main',
    name: 'main',
    key: '/',
    element: <Main />,
  }, 
  {
    path: '/myroom',
    key: 'myroom',
    title: 'myroom',
    name: 'myroom',
    element: <MyPage />,
    isShow: false,
    layout: 'bisection',
  },    
  {
    path: '/error',
    name: 'error',
    title: 'error',
    key: 'error',
    element: <ErrorAuth />,
    isShow: false,
    public: true,
  },
  {
    path: '*',
    name: '404',
    title: '404',
    key: '404',
    element: <Error404 />,
    isShow: false,
  },
  {
    path: '/b2b',
    title: 'B2B',
    key: 'b2b',
    isShow: true,
    layout: 'bisection',
    children: [
      {
        title: '확정',
        name: 'b2bConfirm',
        path: '/b2b/confirm',
        key: 'b2b/confirm',
        layout: 'bisection',
        element: <ProjectConfirm />,
        isShow: true,
      },
      {
        title: '논의중',
        name: 'b2bConsult',
        path: '/b2b/consult',
        key: 'b2b/consult',
        layout: 'bisection',
        element: <ProjectConsult />,
        isShow: true,
      },    
      {
        title: '문의',
        name: 'b2bAsk',
        path: '/b2b/ask',
        key: 'b2b/ask',
        layout: 'bisection',
        element: <ProjectAsk />,
        isShow: true,
      }, 
      {
        title: '드롭',
        name: 'b2bDrop',
        path: '/b2b/drop',
        key: 'b2b/drop',
        layout: 'bisection',
        element: <ProjectDrop />,
        isShow: true,
      },         
    ],     
  },
  {
    path: '/b2c',
    title: 'B2C',
    key: 'b2c',
    isShow: true,
    layout: 'bisection',
    children: [
      {
        title: '공개과정',
        name: 'b2cLecture',
        path: '/b2c/lecture',
        key: 'b2c/lecture',
        layout: 'bisection',
        element: <Lecture />,
        isShow: true,
      },
      {
        title: '성과백신',
        name: 'b2cPml',
        path: '/b2c/pml',
        key: 'b2c/pml',
        layout: 'bisection',
        element: <Pml />,
        isShow: true,
      }, 
      {
        title: '세미나',
        name: 'b2cSeminar',
        path: '/b2c/seminar',
        key: 'b2c/seminar',
        layout: 'bisection',
        element: <Seminar />,
        isShow: true,
      },    
      {
        title: '공개교육과정관리',
        name: 'b2cCommonLecture',
        path: '/b2c/common/lecture',
        key: 'b2c/common/lecture',
        layout: 'bisection',
        element: <LectureCommon />,
        isShow: true,
      }, 
      {
        title: '세미나신청그룹관리',
        name: 'b2cCommonSeminar',
        path: '/b2c/common/seminar',
        key: 'b2c/common/seminar',
        layout: 'bisection',
        element: <SeminarCommon />,
        isShow: true,
      },     
      {
        title: '메일 템플릿 관리',
        name: 'b2cMailTemplate',
        path: '/b2c/common/mail',
        key: 'b2c/common/mail',
        layout: 'bisection',
        element: <MailCommon />,
        isShow: true,
      },         
    ],      
  },  
  {
    path: '/schedule',
    title: '강사일정',
    name: 'schedule',
    key: 'schedule',
    element: <Schedule />,
    isShow: true,
    layout: 'bisection',
  },   
  {
    path: '/lecturer',
    title: '강사목록',
    name: 'lecturer',
    key: 'lecturer',
    element: <Lecturer />,
    isShow: true,
    layout: 'bisection',
  },  
  {
    path: '/customer',
    title: '고객',
    key: 'customer',
    isShow: true,
    layout: 'bisection',
    children: [
      {
        title: '고객사',
        name: 'customerCompany',
        path: '/customer/company',
        key: 'customer/company',
        layout: 'bisection',
        element: <Company />,
        isShow: true,
      },
      {
        title: '교육담당자',
        name: 'customerCrm',
        path: '/customer/crm',
        key: 'customer/crm',
        layout: 'bisection',
        element: <Crm />,
        isShow: true,
      },    
      {
        title: '지식수',
        name: 'customerMailing',
        path: '/customer/mailing',
        key: 'customer/mailing',
        layout: 'bisection',
        element: <Mailing />,
        isShow: true,
      }, 
    ],    
  },    
  {
    path: '/parment',
    title: '정산관리',
    key: 'parment',
    isShow: true,
    layout: 'bisection',
    children: [
      {
        title: '매출발행현황',
        name: 'billPublic',
        path: '/parment/bill',
        key: 'parment/bill',
        layout: 'bisection',
        element: <BillPublic />,
        isShow: true,
      },
      {
        title: '외부강사료지급현황',
        name: 'lecturerPayment',
        path: '/parment/lecturer/payment',
        key: 'parment/lecturer/payment',
        layout: 'bisection',
        element: <LecturerPayment />,
        isShow: true,
      },
      {
        title: '외주비현황',
        name: 'outsourcing',
        path: '/parment/lecturer/outsourcing',
        key: 'parment/lecturer/outsourcing',
        layout: 'bisection',
        element: <Outsourcing />,
        isShow: true,
      }
    ]
  },   
  {
    path: '/management',
    title: 'Management',
    key: 'management',
    isShow: true,
    layout: 'bisection',
    children: [
      {
        title: '매출',
        name: 'salesprice',
        path: '/management/sales',
        key: 'management/sales',
        layout: 'bisection',
        element: <SalesPirce />,
        isShow: true,
      },
      {
        title: '강의평가',
        name: 'saleslecture',
        path: '/management/lecture',
        key: 'management/lecture',
        layout: 'bisection',
        element: <SalesLecture />,
        isShow: true,
      },
    ]
  },    
  {
    path: '/operate',
    title: '운영',
    name: 'operate',
    key: 'operate',
    element: <Operate />,
    isShow: true,
    layout: 'bisection',       
  },   
  {
    path: '/common',
    title: '기초정보관리',
    key: 'common',
    isShow: true,
    layout: 'bisection',
    children: [
      {
        title: '강의구분',
        name: 'commonLectureGubun',
        path: '/common/lecture/gubun',
        key: 'common/lecture/gubun',
        layout: 'bisection',
        element: <LectureGubun />,
        isShow: true,
      },
      {
        title: '강의주제',
        name: 'commonLectureSubject',
        path: '/common/lecture/subject',
        key: 'common/lecture/subject',
        layout: 'bisection',
        element: <LectureSubject />,
        isShow: true,
      },    
      {
        title: '교육대상',
        name: 'commonLectureTarget',
        path: '/common/lecture/target',
        key: 'common/lecture/target',
        layout: 'bisection',
        element: <LectureTarget />,
        isShow: true,
      },     
      {
        title: '강의장소',
        name: 'commonLectureStudio',
        path: '/common/lecture/studio',
        key: 'common/lecture/studio',
        layout: 'bisection',
        element: <LectureStudio />,
        isShow: true,
      },     
      {
        title: '강사분류',
        name: 'commonLecturerGroup',
        path: '/common/lecturer/group',
        key: 'common/lecturer/group',
        layout: 'bisection',
        element: <LecturerGroup />,
        isShow: true,
      },     
      {
        title: '업종',
        name: 'commonBizCategory',
        path: '/common/biz/category',
        key: 'common/biz/category',
        layout: 'bisection',
        element: <BizCategory />,
        isShow: true,
      },                
    ],    
  },    
  {
    path: '/user',
    title: '사용자관리',
    name: 'user',
    key: 'user',
    element: <User />,
    isShow: true,
    public: true,
    layout: 'bisection',
    rule: '103,999',    
  }, 
  {
    title: '고객사 포인트관리',
    name: 'customerCompanyPoint',
    path: '/customer/company/point/:id',
    key: 'customer/company/point/:id',
    layout: 'bisection',
    element: <CompanyPoint />,
    isShow: false,   
  },  
  {
    title: '고객사 선납금관리',
    name: 'customerCompanyMoney',
    path: '/customer/company/money/:id',
    key: 'customer/company/money/:id',
    layout: 'bisection',
    element: <CompanyMoney />,
    isShow: false,   
  }, 
  {
    title: '공개과정 교육생정보',
    name: 'lectureStudent',
    path: '/b2c/lecture/student/:id',
    key: 'b2c/lecture/student/:id',
    layout: 'bisection',
    element: <LectureStudent />,
    isShow: false,   
  },     
  {
    title: '성과백신 교육생정보',
    name: 'pmlStudent',
    path: '/b2c/pml/student/:id',
    key: 'b2c/pml/student/:id',
    layout: 'bisection',
    element: <PmlStudent />,
    isShow: false,   
  }, 
  {
    title: '세미나 교육생정보',
    name: 'seminarStudent',
    path: '/b2c/seminar/student/:id',
    key: 'b2c/seminar/student/:id',
    layout: 'bisection',
    element: <SeminarStudent />,
    isShow: false,   
  },         
];



const flatPath = (acc: Array<Path>, path: Path) => {
  if (!path.children) {
    acc.push(path);
    return acc;
  }
  path.children.map((p) => {
    flatPath(acc, p);
  });
  return acc;
};

const generateObjectPath = (obj: Partial<PathObject>, path: Path): void => {
  if (path.name) {
    obj[path.name] = path.path;
  }

  path.children?.map((p) => {
    generateObjectPath(obj, p);
  });
};

const flatPaths = paths.map((path) => flatPath([], path)).flat();

const getPathObject = (): PathObject => {
  const obj = {};

  paths.forEach((path) => generateObjectPath(obj, path));

  return obj as PathObject;
};

const pathObject: PathObject = getPathObject();

const getPage = (pathName: string) => {
  return flatPaths.filter((p) => {
    if (p.path) {
      return matchPath(p.key, pathName);
    }

    return null;
  });
};

const Paths = {
  paths,
  flatPaths,
  getProperty(pathName: string, property: string): any {
    const page = getPage(pathName);

    if (page.length > 0) {
      return page[0][property];
    }
    return null;
  },
  is404Page(pathName: string) {
    const page = getPage(pathName);

    return page.length === 0;
  },
  pathObject,
};

export default Paths;
